import type { ApolloError } from '@apollo/client';
import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, theme } from '@hover/blueprint';
import { useMediaQuery } from '@hover/blueprint/chakra';
import { useActor, useInterpret } from '@xstate/react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSpring } from 'react-spring';
import type { StateValue } from 'xstate';

import {
  RenderingRequest,
  useShouldShowRenderingRequest,
} from 'src/modules/designStudio/components/DesignMenu/RenderingRequest';
import { MaybeHideCookieBanner } from 'src/modules/designStudio/components/DesignStudio/MaybeHideCookieBanner/MaybeHideCookieBanner';
import { Meta } from 'src/modules/designStudio/components/DesignStudio/Meta';
import { UILayer } from 'src/modules/designStudio/components/DesignStudio/UILayer';
import { FadingContainer } from 'src/modules/designStudio/components/FadingContainer';
import { InteractiveRenderingErrorPage } from 'src/modules/designStudio/components/InteractiveRenderingErrorPage/InteractiveRenderingErrorPage';
import { InteractiveRenderingNotFoundPage } from 'src/modules/designStudio/components/InteractiveRenderingNotFoundPage/InteractiveRenderingNotFoundPage';
import { ShareErrorPage } from 'src/modules/designStudio/components/ShareErrorPage';
import { useToolbarDisplaySettings } from 'src/modules/designStudio/hooks';
import { createMachine } from 'src/modules/designStudio/machines/app';
import {
  DesignStudioServiceProvider,
  HistoryProvider,
  InteractiveRenderingProvider,
  InteractiveRenderingStateContext,
  MacheteBuildingProvider,
  Mode,
  ModeProvider,
  ProductCatalogProvider,
  productCatalogContext,
} from 'src/modules/designStudio/providers';
import type { TrackingMetadata } from 'src/modules/designStudio/types';
import { NotFound } from 'src/modules/notFound';
import { FirebaseWarningStyles } from 'src/shared/components';
import { DEFAULT_CARD_HEIGHT_PERCENTAGE } from 'src/shared/components/SlideUpCard/types';
import { ErrorScope, FeatureFlag } from 'src/shared/constants';
import { ErrorTagName } from 'src/shared/constants/ErrorTag';
import {
  TranslationNamespace,
  useErrorScope,
  useErrorTag,
  useMobileErrorTracking,
  useQuery,
  useQueryParam,
  useReactNativeWebView,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { useToaster } from 'src/shared/providers/Toaster';
import type { PageViewed } from 'src/shared/telemetry/generated/segment';

import { Menu } from '../../constants';
import type { ExtendedMacheteViewer } from '../../helpers/machete';
import { FullscreenIframeButton } from '../DesignMenu/FullscreenIframeButton';

import { Building } from './Building';
import { DesignStudioTitle } from './DesignStudioTitle';
import { LandscapeLogo } from './GuestMode/LandscapeLogo';
import { InteractiveRenderingCanvas } from './InteractiveRendering/Canvas';
import { LoadingPage } from './LoadingPage';
import {
  DesignStudioContainer,
  MainContainer,
  preventOverscrollStyle,
} from './styled';

const getShowDesignChanges = ({
  mode,
  shareFlag,
  proFlag,
}: {
  mode: Mode;
  shareFlag: boolean;
  proFlag: boolean;
}) => (mode === Mode.SHARE && shareFlag) || (mode === Mode.PRO_VIEW && proFlag);

const Placeholder = styled(Box)`
  background-color: ${theme.colors.primary.base};
`;

const renderPlaceholder = (error?: ApolloError) =>
  error ? (
    <ShareErrorPage />
  ) : (
    <Placeholder
      bottom={0}
      left={0}
      position="absolute"
      right={0}
      testId="placeholder"
      top={0}
    />
  );

export interface DesignStudioProps {
  finishLoadingAnimation: () => void;
  finishLoadingBuilding: () => void;
  isJobNotFound: boolean;
  isLoading: boolean;
  isOnline: boolean;
  isWebView: boolean;
  jobId?: number;
  mode: Mode;
  requestingOrgName?: string;
  trackingMetadata?: TrackingMetadata;
  userId?: number;
  appState?: StateValue;
}

const useCanvasHeight = ({
  menu,
  showDesignChanges,
  cardHeight,
}: {
  menu: Menu | null;
  showDesignChanges: boolean;
  cardHeight?: number;
}) => {
  const [isMobileOrTablet] = useMediaQuery('(max-width: 834px)');
  const [canvasHeight, setCanvasHeight] = useState<string>('100%');

  useEffect(() => {
    if (showDesignChanges) {
      if (cardHeight && cardHeight >= DEFAULT_CARD_HEIGHT_PERCENTAGE) {
        setCanvasHeight('60%');
        return;
      }
    } else if (isMobileOrTablet && menu) {
      setCanvasHeight('60%');
      return;
    }

    setCanvasHeight('100%');
  }, [isMobileOrTablet, menu, showDesignChanges, cardHeight]);

  return canvasHeight;
};

const PureDesignStudio = (props: DesignStudioProps) => {
  const {
    jobId,
    isWebView,
    isLoading,
    isJobNotFound,
    finishLoadingAnimation,
    finishLoadingBuilding,
    isOnline,
    requestingOrgName,
    trackingMetadata,
    mode,
    userId,
    appState,
  } = props;

  const isPreviewMode = mode === Mode.PREVIEW;
  const handleFinishLoadingAnimation = isPreviewMode
    ? undefined
    : finishLoadingAnimation;
  const [viewer, setViewer] = useState<ExtendedMacheteViewer | null>(null);
  const showDesignChangesPro =
    useTreatment(FeatureFlag.DesignStudio.DS_SHOW_DESIGN_CHANGES_IN_PRO) ===
    'on';
  const showDesignChangesShare =
    useTreatment(FeatureFlag.DesignStudio.DS_SHOW_DESIGN_CHANGES_IN_SHARE) ===
    'on';
  const showDesignChanges = getShowDesignChanges({
    mode,
    shareFlag: showDesignChangesShare,
    proFlag: showDesignChangesPro,
  });
  const [menu, setMenu] = useState<Menu | null>(
    showDesignChanges ? Menu.SHOW_DESIGN : null,
  );
  const [hasClosedDesignMenu, setHasClosedDesignMenu] = useState(false);
  const { loadError: interactiveRenderingLoadError, renderingFileNotFound } =
    useContext(InteractiveRenderingStateContext);

  const closeDesignMenu = () => {
    if (!hasClosedDesignMenu) {
      setHasClosedDesignMenu(true);
    }
    setMenu(null);
  };
  const { textures, geometry, metadata, authToken, shareError } = useContext(
    productCatalogContext,
  );

  const [cardHeight, setCardHeight] = useState(DEFAULT_CARD_HEIGHT_PERCENTAGE);
  const { shouldDisplayToolbar } = useToolbarDisplaySettings();
  const shouldShowRenderingRequest = useShouldShowRenderingRequest();

  const buildingTransition = useSpring({
    opacity: isLoading ? 0 : 1,
    transform: isLoading ? 'scale(1.2)' : 'scale(1)',
  });

  // Not sure if this is the best place for this, but this
  // controls the canvas height / resize depending on the menu state
  const canvasHeight = useCanvasHeight({
    menu,
    showDesignChanges,
    cardHeight,
  });

  useEffect(() => {
    if (showDesignChanges) {
      setMenu(Menu.SHOW_DESIGN);
    }
  }, [showDesignChanges]);

  const hasRequiredDataForBuilding = !!geometry && !!metadata && !!textures;

  if (mode === Mode.SHARE && !authToken) {
    return renderPlaceholder(shareError);
  }

  if (mode === Mode.INTERACTIVE_RENDERING && interactiveRenderingLoadError) {
    return <InteractiveRenderingErrorPage />;
  }

  if (mode === Mode.INTERACTIVE_RENDERING && renderingFileNotFound) {
    return <InteractiveRenderingNotFoundPage />;
  }

  return (
    <>
      {/* If mobile app, restrict user scaling of document */}
      {isWebView && <Meta />}
      <DesignStudioTitle />
      <FirebaseWarningStyles />
      <DesignStudioContainer
        bottom={0}
        height="100%"
        position="fixed"
        testId="design-studio-container"
        top={0}
        width="100%"
      >
        <LandscapeLogo isLoading={isLoading} />
        <FullscreenIframeButton loading={isLoading} />
        <MaybeHideCookieBanner />
        <Global styles={preventOverscrollStyle} />
        {isJobNotFound ? (
          <NotFound />
        ) : (
          <MainContainer style={buildingTransition}>
            {mode === Mode.INTERACTIVE_RENDERING ? (
              <InteractiveRenderingCanvas
                appState={appState}
                finishLoadingRendering={finishLoadingBuilding}
                menu={menu}
                setMenu={setMenu}
              />
            ) : (
              <>
                {shouldShowRenderingRequest && (
                  <RenderingRequest
                    closeDesignMenu={closeDesignMenu}
                    hasClosedDesignMenu={hasClosedDesignMenu}
                    isDesignMenuOpen={!!menu}
                    jobId={jobId}
                  />
                )}

                {hasRequiredDataForBuilding && (
                  <Building
                    canvasHeight={canvasHeight}
                    geometry={geometry}
                    jobId={jobId}
                    menu={menu}
                    metadata={metadata}
                    setLoadingBuilding={finishLoadingBuilding}
                    setViewer={setViewer}
                    textures={textures}
                    trackingMetadata={trackingMetadata}
                  />
                )}
              </>
            )}
            <UILayer
              closeMenu={closeDesignMenu}
              isOnline={isOnline}
              jobId={jobId}
              loading={isLoading}
              menu={menu}
              mode={mode}
              requestingOrgName={requestingOrgName}
              setCardHeight={setCardHeight}
              setMenu={setMenu}
              shouldDisplayToolbar={shouldDisplayToolbar}
              showDesignChanges={showDesignChanges}
              userId={userId}
              viewer={viewer}
            />
          </MainContainer>
        )}
        <FadingContainer visible={isLoading}>
          <LoadingPage
            finishLoadingAnimation={handleFinishLoadingAnimation}
            simple={isPreviewMode}
          />
        </FadingContainer>
      </DesignStudioContainer>
    </>
  );
};

const DesignStudio = ({ mode }: { mode: Mode }) => {
  const [isWebView] = useReactNativeWebView();

  // Sets a tag on Sentry with the key of scope for easy filtering
  useErrorScope(ErrorScope.DESIGN_STUDIO);

  // Sets a tag on Sentry with key of design_studio_mode for easy filtering
  useErrorTag(ErrorTagName.DESIGN_STUDIO_MODE, mode);

  // Sets tags on Sentry with mobile app data, if in mobile app
  useMobileErrorTracking();

  const { id } = useParams<{ id: string }>();
  const { userId: userIdParam } = useParams<{ userId: string }>();
  const viewUserId = parseInt(userIdParam, 10);

  const isJobIdRequired = mode !== Mode.GUEST && mode !== Mode.PREVIEW;
  const jobId = isJobIdRequired ? parseInt(id, 10) : undefined;

  const partnerQuery = useQueryParam('partner');

  const queryParams = useQuery();
  const shareToken = queryParams.get('share_token') ?? undefined;

  // set job id in context for tracking
  const { mergeMetadata } = useTrackingMetadata();
  const { addToast, removeToast } = useToaster();

  const translator = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const appService = useInterpret(() =>
    createMachine({
      mode,
      jobId,
      addToast,
      removeToast,
      translator,
      isOnline: true,
    }),
  );

  // The appService must be started within useEffect to avoid starting the
  // machine when server side rendering. When started, appService triggers
  // calls to graphql that will result in an attempt to access local storage
  // which will throw an error when done on the server.
  useEffect(() => {
    appService.start();
  }, [appService]);

  const [state, send] = useActor(appService);

  const { isOnline, requestingOrgName, partnerId } = state.context;

  useEffect(() => {
    const metadata: PageViewed = {
      job_id: jobId,
      design_studio_mode: mode,
    };
    if (partnerId) {
      metadata.partner_id = partnerId;
    }

    mergeMetadata(metadata);
    // eslint-disable-next-line
  }, [jobId, mode, partnerId]);

  const isLoading = state.value !== 'idle' && state.value !== 'notfound';

  const isJobNotFound = state.value === 'notFound';
  const finishLoadingAnimation = useCallback(
    () => send('FINISH_LOADING_ANIMATION'),
    [send],
  );
  const finishLoadingBuilding = useCallback(
    () => send('FINISH_LOADING_BUILDING'),
    [send],
  );

  const props = {
    jobId,
    isWebView,
    isLoading,
    isJobNotFound,
    finishLoadingAnimation,
    finishLoadingBuilding,
    isOnline,
    requestingOrgName,
    mode,
    appState: state.value,
  };

  const trackingMetadata = { partner_id: partnerId };

  return (
    <DesignStudioServiceProvider appService={appService}>
      <MacheteBuildingProvider>
        <ModeProvider
          mode={mode}
          partnerId={partnerId}
          partnerQuery={partnerQuery}
        >
          <ProductCatalogProvider jobId={jobId} shareToken={shareToken}>
            <InteractiveRenderingProvider>
              <HistoryProvider
                jobId={jobId}
                mode={mode}
                shareToken={shareToken}
                viewUserId={viewUserId}
              >
                <PureDesignStudio
                  {...props}
                  trackingMetadata={trackingMetadata}
                />
              </HistoryProvider>
            </InteractiveRenderingProvider>
          </ProductCatalogProvider>
        </ModeProvider>
      </MacheteBuildingProvider>
    </DesignStudioServiceProvider>
  );
};

export { DesignStudio, PureDesignStudio, useCanvasHeight };
